"use client";

import { useEffect, useState } from "react";
import { SlidersHorizontal, X } from "lucide-react";
interface FilterShellProps {
  /** Number of active filters, shown as a badge on the mobile toggle. */
  activeCount: number;
  children: React.ReactNode;
}

// Persist the open state for the tab so the mobile drawer stays open while you
// apply several filters — each filter tap is a page navigation that would
// otherwise remount this component and snap the drawer shut.
const PERSIST_KEY = "iloris-filters-open";

/**
 * Responsive wrapper around the filter sidebar.
 *
 * Desktop (`md+`): renders inline as a normal sidebar (forced open via the
 * `.filter-disclosure` rules in globals.css).
 *
 * Mobile: a "Filters" toggle opens the sidebar as a **bottom-sheet overlay**
 * (see globals.css) — it floats over the page instead of pushing the results
 * down, and stays open across filter taps (persisted in sessionStorage) so you
 * can apply several before dismissing it via the backdrop or the ✕.
 *
 * Built on a native `<details>` element so the sidebar's links/test-ids exist
 * exactly once in the DOM (no portal, no duplicate instance, no hydration
 * layout shift on first paint).
 */
export function FilterShell({
  activeCount,
  children
}: FilterShellProps) {
  // Initialise from the persisted flag *synchronously* in the first render so a
  // remount (e.g. a cross-route filter tap: picking a genre on /browse →
  // /genre/…) paints the drawer already-open — no closed→open flicker. The
  // server always renders closed; a client-side navigation is a fresh client
  // render (no hydration), so it reads the flag and opens on frame one.
  const [open, setOpen] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    try {
      return sessionStorage.getItem(PERSIST_KEY) === "1";
    } catch {
      return false;
    }
  });

  // Lock background scroll while the mobile overlay is open.
  useEffect(() => {
    if (!open) return;
    if (!window.matchMedia("(max-width: 767px)").matches) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);
  const setOpenPersist = (next: boolean) => {
    setOpen(next);
    try {
      sessionStorage.setItem(PERSIST_KEY, next ? "1" : "0");
    } catch {
      /* ignore */
    }
  };
  return <details data-testid="filter-shell" open={open} onToggle={e => setOpenPersist(e.currentTarget.open)} className="filter-disclosure group w-full md:w-64 md:flex-shrink-0">
            <summary data-testid="filter-toggle" className={`flex items-center gap-2 rounded-full border px-4 py-2.5 text-sm font-medium transition-all md:hidden ${activeCount > 0 ? "border-primary bg-primary/10 text-primary" : "border-border bg-card/60 text-foreground hover:border-primary/50"}`}>
                <SlidersHorizontal className="size-4" />
                <span>Filters</span>
                {activeCount > 0 && <span className="inline-flex min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-xs font-bold text-white">
                        {activeCount}
                    </span>}
                <span aria-hidden className="ml-auto text-xs text-landing-faint transition-transform group-open:rotate-180">
                    ▾
                </span>
            </summary>

            {/* Dimmed backdrop — mobile only, shown via CSS when the drawer is open */}
            <button type="button" aria-label="Close filters" onClick={() => setOpenPersist(false)} className="filter-disclosure-backdrop md:hidden" />

            <div className="filter-disclosure-content mt-4 md:mt-0">
                {/* Drawer header with a close affordance — mobile only */}
                <div className="mb-4 flex items-center justify-between md:hidden">
                    <span className="font-heading text-lg font-bold text-landing-heading">
                        Filters
                    </span>
                    <button type="button" onClick={() => setOpenPersist(false)} aria-label="Close filters" className="flex size-11 items-center justify-center rounded-full text-foreground transition-colors hover:bg-muted">
                        <X className="size-5" />
                    </button>
                </div>
                {children}
            </div>
        </details>;
}